import i18n from 'i18next';
import { forEachRight, uniqueId } from 'lodash';

import { getReportSectionPath } from '@/pages/report/Constants';
import {
	ACTIVE_CLASS,
	MAX_ANSWER_TIME_IN_SECONDS,
	VIEW_MODES,
	VISIBLE_CLASS,
} from '@/playlist/Constants';
import {
	COOKIES,
	exerciseTypeCodesNext,
	LAST_CORRECT_STREAK_DAYS,
	PUNCTUATION_CHARACTERS,
} from '@/services/Constants';
import { getQuestionSetPath } from '@/admin/services/Helper';
import { getCookie, isSecure, setCookie } from '@/services/Helper';
import { historyPush } from '@/services/History';

import type { Answer, InstructionStep, Question, QuestionInstructions } from '@/playlist/types';

// ============================================================================
// Answer Helpers
// ============================================================================

export const getMultipleAnswersCount = (currentQuestion: Question | null): number => {
	if (!currentQuestion) return 0;

	const { THE_BLANKS, OPEN_MULTIPLE } = exerciseTypeCodesNext;
	if ([THE_BLANKS, OPEN_MULTIPLE].includes(currentQuestion.type)) {
		const questionDisplay = currentQuestion.question_display;
		if (Array.isArray(questionDisplay)) {
			return questionDisplay.filter((item) => typeof item === 'object').length;
		}
		return 0;
	}

	return currentQuestion.answers.filter((item) => item.is_correct).length;
};

export const preparedData = (preparedMultipleAnswers: Record<string, any>): any[] => {
	return Object.entries(preparedMultipleAnswers).reduce((acc: any[], [k, v]) => {
		acc.push(v);
		return acc;
	}, []);
};

export const removeValue = (
	key: string | number,
	preparedMultipleAnswers: Record<string, any>,
): Record<string, any> => {
	return Object.entries(preparedMultipleAnswers).reduce((acc: Record<string, any>, [k, v]) => {
		if (k !== String(key)) {
			acc[k] = v;
		}
		return acc;
	}, {});
};

export const setFocusOnFieldForMultipleTypes = (
	blankSectionRef: React.RefObject<HTMLElement> | null,
): void => {
	blankSectionRef &&
		blankSectionRef.current &&
		blankSectionRef.current.getElementsByTagName('input')[0] &&
		blankSectionRef.current.getElementsByTagName('input')[0].focus();
};

export const checkDoWeHaveIncorrectAnswer = (
	currentQuestion: Question,
	preparedMultipleAnswers: Record<string, any>,
	isMCType: boolean,
): { isCorrect: boolean } => {
	let hasAtLeastOneIncorrect = false;
	for (const [i, value] of Object.entries(preparedMultipleAnswers)) {
		const currentAnswer = currentQuestion.answers[Number(i)];
		const questionDisplay = currentQuestion.question_display;
		const currentQuestionDisplay = Array.isArray(questionDisplay)
			? questionDisplay[Number(i)]
			: null;

		const answerItem = isMCType
			? currentAnswer?.is_correct && currentAnswer?.answer_display
			: Array.isArray(currentQuestionDisplay)
			? currentQuestionDisplay[1]
			: null;

		if (value.trim() !== (typeof answerItem !== 'string' ? answerItem : answerItem.trim()))
			hasAtLeastOneIncorrect = true;
	}
	return { isCorrect: !hasAtLeastOneIncorrect };
};

export const checkDoWeHaveAllCorrectAnswers = (
	currentQuestion: Question,
	preparedMultipleAnswers: Record<string, any>,
): { isCorrect: boolean } => {
	for (let i = 0; i < currentQuestion.answers.length; i++) {
		if (
			currentQuestion.answers[i].is_correct &&
			currentQuestion.answers[i].answer_display &&
			!preparedMultipleAnswers[i]
		)
			return { isCorrect: false };
	}
	return { isCorrect: true };
};

export const checkDoWeHaveIncorrectAnswers = (
	preparedBlanksMultipleAnswers: Record<string, { isCorrect: boolean }>,
): { isCorrect: boolean } => {
	let hasAtLeastOneIncorrect = false;
	for (const [_, v] of Object.entries(preparedBlanksMultipleAnswers)) {
		if (!v.isCorrect) hasAtLeastOneIncorrect = true;
	}
	return { isCorrect: !hasAtLeastOneIncorrect };
};

// ============================================================================
// Dictate Answer Validation
// ============================================================================

interface ContentObject {
	case_sensitive?: boolean;
	punctuation_sensitive?: boolean;
	space_sensitive?: boolean;
}

export const isDictateAnswerCorrect = (
	currentQuestion: Question,
	userAnswer: string,
): { isCorrect: boolean } => {
	const answers = currentQuestion.answers.reduce((acc: string[], item) => {
		if (item.answer_variations) {
			item.answer_variations.forEach((el) => {
				acc.push(el.answer_text);
			});
		}
		acc.push(item.answer_display);
		return acc;
	}, []);

	const questionDisplay = currentQuestion.question_display;
	let contentObject: ContentObject = {};
	if (Array.isArray(questionDisplay)) {
		const found = questionDisplay.find((el) => typeof el === 'object');
		if (found && typeof found === 'object' && Array.isArray(found) && found[2]) {
			contentObject = found[2] as ContentObject;
		}
	}

	const isCorrect = getIsCorrect(userAnswer, '', contentObject, answers);

	return { isCorrect: !!isCorrect };
};

const removePunctuation_ = (word: string[]): string => {
	return word.filter((el) => PUNCTUATION_CHARACTERS.indexOf(el) === -1).join('');
};

const makeLowerCase_ = (word: string): string => {
	return word.toLowerCase();
};

const removeSpaces_ = (word: string): string => {
	return word.replace(/ /g, '');
};

export const getIsCorrect = (
	value: string | null,
	answerW: string,
	{ case_sensitive, punctuation_sensitive, space_sensitive }: ContentObject,
	extraAnswerVariants: string[],
): number => {
	const splittedAnswers = extraAnswerVariants.map((item) => item.trim().replace('\u2019', "'"));
	const splittedProposedWord = value && value.split('');
	let finalAnswers = [...splittedAnswers];
	let proposedWord_ = value && value.trim().replace('\u2019', "'");

	if (!punctuation_sensitive && value && splittedProposedWord) {
		proposedWord_ = removePunctuation_(splittedProposedWord);
		finalAnswers = finalAnswers.map((answer) => removePunctuation_(answer.split('')));
	}

	if (!case_sensitive && proposedWord_) {
		proposedWord_ = makeLowerCase_(proposedWord_);
		finalAnswers = finalAnswers.map((answer) => makeLowerCase_(answer));
	}

	if (!space_sensitive && proposedWord_) {
		proposedWord_ = removeSpaces_(proposedWord_);
		finalAnswers = finalAnswers.map((answer) => removeSpaces_(answer));
	}

	if (!proposedWord_) return 0;

	return finalAnswers.filter((item) => item.trim() === proposedWord_.trim()).length;
};

export const getExtraAnswersOptions = (
	answersArr: Answer[],
	answerId: string | number,
): string[] => {
	return answersArr.reduce((acc: string[], answer) => {
		if (answer.id === answerId) {
			acc.push(answer.answer_display);
			if (answer.answer_variations && answer.answer_variations.length) {
				answer.answer_variations.forEach((item) => acc.push(item.answer_text));
			}
		}
		return acc;
	}, []);
};

// ============================================================================
// Answer Helpers
// ============================================================================

export const getMaxItemLength = (items: (string | null | undefined)[]): number => {
	return items.reduce((acc, item) => {
		const itemLength = item?.length ?? 0;
		if (acc < itemLength) {
			return itemLength;
		}
		return acc;
	}, 0);
};

export const getCorrectAnswer = (answers: Answer[]): Answer | Record<string, never> =>
	answers.reduce((acc: Answer | Record<string, never>, item) => {
		if (item.is_correct) acc = item;
		return acc;
	}, {});

export const getCorrectAnswers = (answers: Answer[]): string[] =>
	answers.reduce((acc: string[], item) => {
		if (item.is_correct) acc.push(item.answer_display);
		return acc;
	}, []);

// ============================================================================
// Score Helpers
// ============================================================================

interface ScoreDataInput {
	questions?: Question[];
	correct_answers_count?: number;
	wrong_answers_count?: number;
}

interface ScoreData {
	correct: number;
	incorrect: number;
	questionsLeft: Question[];
}

export const getScoreData = ({
	questions,
	correct_answers_count = 0,
	wrong_answers_count = 0,
}: ScoreDataInput): ScoreData => {
	const normalizedQuestions = questions ?? [];

	return {
		correct: correct_answers_count,
		incorrect: wrong_answers_count,
		questionsLeft: normalizedQuestions.slice(correct_answers_count + wrong_answers_count),
	};
};

// ============================================================================
// Image Helpers
// ============================================================================

export const getImageUrlsFromContent = (content: string | string[]): string[] => {
	const srcRegex = /<img[^>]*src=['"]([^'"]*)['"]/g;

	const srcAttributes: string[] = [];
	let match;

	if (Array.isArray(content)) {
		content.forEach((item) => {
			if (typeof item == 'string') {
				while ((match = srcRegex.exec(item)) !== null) {
					srcAttributes.push(match[1]);
				}
			}
		});
	} else {
		while ((match = srcRegex.exec(content)) !== null) {
			srcAttributes.push(match[1]);
		}
	}

	return srcAttributes;
};

export const isCharactersLess = (content: string | any[], checkLength: number): boolean => {
	let string = '';

	if (Array.isArray(content)) {
		content.forEach((item) => {
			if (typeof item == 'string') {
				string += item;
			}
		});
	} else {
		string += content;
	}

	return string.length <= checkLength;
};

// ============================================================================
// Instruction Helpers
// ============================================================================

export const getInstructionOnboardingSteps = (
	instructionAudioOn: boolean,
): QuestionInstructions[] => {
	const onboardingInstructions: InstructionStep[] = [
		{
			id: uniqueId(),
			message: i18n.t('exercise.instruction.onboarding.message_1'),
		},
		{
			id: uniqueId(),
			message: i18n.t('exercise.instruction.onboarding.message_2'),
		},
	];

	if (instructionAudioOn) {
		onboardingInstructions.push({
			id: uniqueId(),
			message: i18n.t('exercise.instruction.onboarding.message_3'),
		});
	}

	return [
		{
			isOnboarding: true,
			instructions: onboardingInstructions,
		},
		{
			isOnboardingCheck: true,
			instructionsAudioOn: [
				{
					id: uniqueId(),
					message: i18n.t('exercise.instruction.onboarding_check.audio_on_message_1'),
					audio_url:
						'https://storage.googleapis.com/np68-backend-staging/question_instructions/642184_340_da2b0ba7dd9dd9530ac0de8ef80a12d8.mp3',
				},
				{
					id: uniqueId(),
					message: i18n.t('exercise.instruction.onboarding_check.audio_on_message_2'),
					audio_url:
						'https://storage.googleapis.com/np68-backend-staging/question_instructions/642184_341_175c15308fdce855b994b6c8b760968e.mp3',
				},
			],
			instructionsAudioOff: [
				{
					id: uniqueId(),
					message: i18n.t('exercise.instruction.onboarding_check.audio_off_message_1'),
				},
				{
					id: uniqueId(),
					message: i18n.t('exercise.instruction.onboarding_check.audio_off_message_2'),
				},
			],
			instructions: [],
		},
	];
};

export const getInstructionSteps = (
	question: Question | null,
	givenFeedback: boolean,
): QuestionInstructions[] => {
	const instructionSteps: QuestionInstructions[] = [
		...(question?.question_instructions || []),
	] as QuestionInstructions[];

	if (
		instructionSteps.length &&
		!givenFeedback &&
		!instructionSteps[instructionSteps.length - 1].isFeedback
	) {
		instructionSteps.push({
			isFeedback: true,
			instructions: [{ id: uniqueId(), isFeedbackMessage: true }],
		});
	} else if (givenFeedback && instructionSteps[instructionSteps.length - 1]?.isFeedback) {
		instructionSteps.pop();
	}

	return instructionSteps;
};

interface ViewedInstructionData {
	totalMessages: number;
	totalSteps: number;
	totalMessagesViewed: number;
	totalStepsViewed: number;
	totalStepsViewedPercentage: number;
}

export const getViewedInstructionData = (
	questionInstructions: QuestionInstructions[] | null,
): ViewedInstructionData => {
	let totalMessages = 0;
	let totalMessagesViewed = 0;

	const totalStepsViewed =
		questionInstructions?.reduce((stepsAcc, item) => {
			if (item.isFeedback || item.isOnboarding || item.isOnboardingCheck) return stepsAcc;

			const messagesViewedPerStep = item.instructions.reduce((messagesAcc, item) => {
				if (item.isViewed) messagesAcc++;
				return messagesAcc;
			}, 0);

			totalMessages += item.instructions.length;

			if (messagesViewedPerStep > 0) {
				totalMessagesViewed += messagesViewedPerStep;
				stepsAcc++;
			}

			return stepsAcc;
		}, 0) || 0;

	const totalSteps =
		questionInstructions?.filter(
			(item) => !item.isFeedback && !item.isOnboarding && !item.isOnboardingCheck,
		).length || 0;

	const totalStepsViewedPercentage =
		totalSteps > 0 ? Math.round((totalStepsViewed / totalSteps) * 100) : 0;

	return {
		totalMessages,
		totalSteps,
		totalMessagesViewed,
		totalStepsViewed,
		totalStepsViewedPercentage,
	};
};

// ============================================================================
// Reading Mode Helpers
// ============================================================================

export const calculateReadingProgress = (content: HTMLElement): number => {
	const contentLength = content.innerHTML.length;
	const activeContent = content.querySelectorAll(`.${ACTIVE_CLASS}, .${VISIBLE_CLASS}`);

	let activeContentLength = 0;
	for (let i = 0; i < activeContent.length; i++) {
		activeContentLength += activeContent[i].outerHTML.length;
	}

	return Math.round((activeContentLength / contentLength) * 100);
};

export const prepareTextForReading = (htmlString: string | null): string => {
	if (!htmlString) return '';

	const parser = new DOMParser();
	const doc = parser.parseFromString(htmlString, 'text/html');
	const body = doc.body;

	// Remove first image or figure element
	const children = body.children;
	for (let i = 0; i < children.length; i++) {
		const element = children[i];
		if (['img', 'figure'].includes(element.tagName?.toLowerCase())) {
			element.remove();
			break;
		}
	}

	// Remove empty paragraphs from the end
	forEachRight(Array.from(body.children), (element) => {
		if (
			element.tagName?.toLowerCase() === 'p' &&
			(element as HTMLElement).textContent?.trim().length === 0
		) {
			element.remove();
		}
	});

	return body.innerHTML;
};

export const questionHasInstructionAudio = (question: Question | null): boolean => {
	if (!question?.question_instructions) return false;

	return question.question_instructions.some((item: any) =>
		item.instructions?.some((el: any) => el.audio_url),
	);
};

// ============================================================================
// Time Helpers
// ============================================================================

export const getAnswerTimeInSeconds = (startTimestamp: number): number => {
	const seconds = Math.floor(Date.now() / 1000) - Math.floor(startTimestamp / 1000);
	return seconds > MAX_ANSWER_TIME_IN_SECONDS ? MAX_ANSWER_TIME_IN_SECONDS : seconds;
};

// ============================================================================
// Navigation Helpers
// ============================================================================

export const handleClickBack = (
	viewMode: string | null,
	playlistId: string | number,
	freePlayCategoryPath?: string | null,
): void => {
	if (viewMode === VIEW_MODES.REPORT || viewMode === VIEW_MODES.REPLAY) {
		historyPush(getReportSectionPath(String(playlistId)));
	} else if (viewMode === VIEW_MODES.PREVIEW) {
		historyPush(getQuestionSetPath(String(playlistId)));
	} else if (freePlayCategoryPath) {
		historyPush(freePlayCategoryPath);
	} else {
		historyPush('/');
	}
};

// ============================================================================
// Correct Streak Helpers
// ============================================================================

export const setLastCorrectStreak = (lastCorrectStreak: number): void => {
	setCookie(COOKIES.lastCorrectStreak, lastCorrectStreak, {
		expires: LAST_CORRECT_STREAK_DAYS,
		secure: isSecure(),
	});
};

export const getLastCorrectStreak = (): number => {
	const lastCorrectStreak = getCookie(COOKIES.lastCorrectStreak);
	return lastCorrectStreak ? parseInt(lastCorrectStreak) : 0;
};
